import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type TimestampCaptureMoment = 'onReturn' | 'onStartTyping';

// Sidebar splitter bounds (parity: macOS NavigationSplitView column resize).
export const SIDEBAR_WIDTH_DEFAULT = 320;
export const SIDEBAR_WIDTH_MIN = 240;
export const SIDEBAR_WIDTH_MAX = 500;
export const clampSidebarWidth = (w: number) =>
  Math.min(SIDEBAR_WIDTH_MAX, Math.max(SIDEBAR_WIDTH_MIN, Math.round(w)));

export interface Preferences {
  reverseOrder: boolean;
  timestampCaptureMoment: TimestampCaptureMoment;
  sidebarWidth: number;
  showMilliseconds: boolean;
}

const DEFAULTS: Preferences = {
  reverseOrder: false,
  timestampCaptureMoment: 'onReturn',
  sidebarWidth: SIDEBAR_WIDTH_DEFAULT,
  showMilliseconds: false,
};

const STORAGE_KEY = 'zubi:prefs';

interface PreferencesContextType extends Preferences {
  setReverseOrder: (value: boolean) => void;
  setTimestampCaptureMoment: (value: TimestampCaptureMoment) => void;
  setSidebarWidth: (value: number) => void;
  setShowMilliseconds: (value: boolean) => void;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

function readSaved(): Preferences {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return {
      reverseOrder: typeof parsed.reverseOrder === 'boolean' ? parsed.reverseOrder : DEFAULTS.reverseOrder,
      timestampCaptureMoment:
        parsed.timestampCaptureMoment === 'onReturn' || parsed.timestampCaptureMoment === 'onStartTyping'
          ? parsed.timestampCaptureMoment
          : DEFAULTS.timestampCaptureMoment,
      sidebarWidth:
        typeof parsed.sidebarWidth === 'number' && Number.isFinite(parsed.sidebarWidth)
          ? clampSidebarWidth(parsed.sidebarWidth)
          : DEFAULTS.sidebarWidth,
      showMilliseconds: typeof parsed.showMilliseconds === 'boolean' ? parsed.showMilliseconds : DEFAULTS.showMilliseconds,
    };
  } catch {
    return DEFAULTS;
  }
}

export const PreferencesProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [prefs, setPrefs] = useState<Preferences>(() => readSaved());

  const setReverseOrder = useCallback((value: boolean) => {
    setPrefs((prev) => ({ ...prev, reverseOrder: value }));
  }, []);

  const setTimestampCaptureMoment = useCallback((value: TimestampCaptureMoment) => {
    setPrefs((prev) => ({ ...prev, timestampCaptureMoment: value }));
  }, []);

  const setSidebarWidth = useCallback((value: number) => {
    setPrefs((prev) => ({ ...prev, sidebarWidth: clampSidebarWidth(value) }));
  }, []);

  const setShowMilliseconds = useCallback((value: boolean) => {
    setPrefs((prev) => ({ ...prev, showMilliseconds: value }));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // Safari private mode etc. — silently ignore, same as ThemeContext.
    }
  }, [prefs]);

  const value = useMemo(
    () => ({ ...prefs, setReverseOrder, setTimestampCaptureMoment, setSidebarWidth, setShowMilliseconds }),
    [prefs, setReverseOrder, setTimestampCaptureMoment, setSidebarWidth, setShowMilliseconds],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

export const usePreferences = (): PreferencesContextType => {
  const ctx = useContext(PreferencesContext);
  if (ctx === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return ctx;
};
